import React from 'react';
import './AnimatedTabs.css';

export interface AnimatedTabItem {
  label: string;
  count?: number;
  icon?: React.ReactNode;
}

export interface AnimatedTabsProps {
  tabs: AnimatedTabItem[];
  activeTab: string;
  onChange: (label: string) => void;
  /** 'line' = underline style (default), 'pill' = animated sliding pill */
  variant?: 'line' | 'pill';
  size?: 'lg' | 'md' | 'sm';
}

// ─── Pill sub-component (hooks always called at top-level) ───────────────────
const PillTabs: React.FC<Omit<AnimatedTabsProps, 'variant'>> = ({ tabs, activeTab, onChange }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const activeTabRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const container = containerRef.current;
    if (container && activeTabRef.current) {
      const { offsetLeft, offsetWidth } = activeTabRef.current;
      const clipLeft = offsetLeft + 16;
      const clipRight = offsetLeft + offsetWidth + 16;
      container.style.clipPath = `inset(0 ${Number(100 - (clipRight / container.offsetWidth) * 100).toFixed()}% 0 ${Number((clipLeft / container.offsetWidth) * 100).toFixed()}% round 17px)`;
    }
  }, [activeTab]);

  return (
    <div className="anim-tabs-wrapper">
      <div ref={containerRef} className="anim-tabs-overlay">
        <div className="anim-tabs-overlay-inner">
          {tabs.map((tab, i) => (
            <button key={i} onClick={() => onChange(tab.label)} className="anim-tab-btn anim-tab-btn--overlay" tabIndex={-1}>
              {tab.label}
              {tab.count !== undefined && <span className="anim-tab-count anim-tab-count--overlay">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="anim-tabs-real">
        {tabs.map((tab, i) => {
          const isActive = activeTab === tab.label;
          return (
            <button
              key={i}
              ref={isActive ? activeTabRef : null}
              onClick={() => onChange(tab.label)}
              className={`anim-tab-btn anim-tab-btn--base ${isActive ? 'active' : ''}`}
              type="button"
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`anim-tab-count ${isActive ? 'anim-tab-count--active' : ''}`}>{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main exported component ─────────────────────────────────────────────────
const AnimatedTabs: React.FC<AnimatedTabsProps> = ({
  tabs,
  activeTab,
  onChange,
  variant = 'line',
  size = 'md',
}) => {
  if (variant === 'pill') {
    return <PillTabs tabs={tabs} activeTab={activeTab} onChange={onChange} size={size} />;
  }

  // line variant
  return (
    <div className={`atabs-line atabs-line--${size}`}>
      {tabs.map((tab, i) => {
        const isActive = activeTab === tab.label;
        return (
          <button
            key={i}
            className={`atabs-line-btn ${isActive ? 'atabs-line-btn--active' : ''}`}
            onClick={() => onChange(tab.label)}
            type="button"
          >
            {tab.icon && <span className="atabs-icon">{tab.icon}</span>}
            {tab.label}
            {tab.count !== undefined && (
              <span className={`atabs-badge ${isActive ? 'atabs-badge--active' : ''}`}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default AnimatedTabs;
